import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import logo from '../../assets/logo.png';
import './DashboardLogin.css';

/** Allowed dashboard admin email - only this user can access the dashboard */
const DASHBOARD_ADMIN_EMAIL = 'ayrineluxe@gmail.com';

/** Session storage key – dashboard access only when user logs in via this form (email + password) */
const DASHBOARD_SESSION_KEY = 'ayrine_dashboard_session';

export default function DashboardLogin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Only redirect if user explicitly logged in via this form (has dashboard session)
  // Google login on main site does NOT grant dashboard access
  const hasDashboardSession = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(DASHBOARD_SESSION_KEY);
  if (!loading && user && !user.is_anonymous && user.email === DASHBOARD_ADMIN_EMAIL && hasDashboardSession) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;

      if (data?.user?.email !== DASHBOARD_ADMIN_EMAIL) {
        await supabase.auth.signOut();
        throw new Error('Access denied. This login is for dashboard administrators only.');
      }

      sessionStorage.setItem(DASHBOARD_SESSION_KEY, '1');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-login-page">
        <div className="dashboard-login-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-login-page">
      <div className="dashboard-login-card">
        <div className="dashboard-login-logo">
          <img src={logo} alt="Ayrine" />
        </div>
        <h1>Dashboard Login</h1>
        <p className="dashboard-login-subtitle">Admin access only</p>

        <form onSubmit={handleSubmit} className="dashboard-login-form">
          {error && <p className="dashboard-login-error">{error}</p>}

          <label>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ayrineluxe@gmail.com"
              required
              autoComplete="email"
            />
          </label>

          <label>
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </label>

          <button type="submit" disabled={submitting}>
            {submitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <a href="/" className="dashboard-login-back">← Back to store</a>
      </div>
    </div>
  );
}
